import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useLocalizedNavigate } from '@/shared/hooks'
import { CheckCircle, Loader2, ArrowRight, Rocket } from 'lucide-react'

type PaymentType = 'scoping' | 'development'

export function PaymentSuccessPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const [searchParams] = useSearchParams()
  const [isVerifying, setIsVerifying] = useState(true)
  const [isSuccess, setIsSuccess] = useState(false)

  const sessionId = searchParams.get('session_id')
  const projectShortId = searchParams.get('project')
  const paymentType = (searchParams.get('type') as PaymentType) || 'scoping'

  useEffect(() => {
    // Simulate verification delay (webhook will handle actual payment confirmation)
    // In production, the webhook updates the payment status
    const timer = setTimeout(() => {
      setIsVerifying(false)
      setIsSuccess(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [sessionId])

  const handleContinue = () => {
    if (projectShortId) {
      if (paymentType === 'scoping') {
        // Redirect to scoping chat with NicolAI
        navigate(`/scoping/${projectShortId}`)
      } else {
        // Add activated param for development payments to show confirmation toast
        navigate(`/p/${projectShortId}?activated=true`)
      }
    } else {
      navigate('/dashboard')
    }
  }

  // Get payment-type specific messages
  const getSuccessTitle = () => {
    if (paymentType === 'development') {
      return t('payment.developmentSuccess')
    }
    return t('payment.success')
  }

  const getSuccessDescription = () => {
    if (paymentType === 'development') {
      return t('payment.developmentSuccessDescription')
    }
    return t('payment.successDescription')
  }

  const getButtonText = () => {
    if (paymentType === 'development') {
      return t('payment.viewProject')
    }
    return t('payment.continueToProject')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-secondary p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        {isVerifying ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              {t('payment.verifying')}
            </h1>
            <p className="text-sm text-foreground-muted">
              {t('payment.verifyingDescription')}
            </p>
          </div>
        ) : isSuccess ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              {paymentType === 'development' ? (
                <Rocket className="h-8 w-8 text-green-600" />
              ) : (
                <CheckCircle className="h-8 w-8 text-green-600" />
              )}
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              {getSuccessTitle()}
            </h1>
            <p className="text-sm text-foreground-muted">
              {getSuccessDescription()}
            </p>
            <button
              onClick={handleContinue}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent/90"
            >
              {getButtonText()}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 text-center">
            <h1 className="text-xl font-semibold text-foreground">
              {t('payment.error')}
            </h1>
            <p className="text-sm text-foreground-muted">
              {t('payment.errorDescription')}
            </p>
            <button
              onClick={handleContinue}
              className="mt-4 rounded-lg border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
            >
              {t('payment.backToDashboard')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
